import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';

export interface DeployEnvironment {
  /** 'dev' | 'staging' | 'prod'. */
  env: 'dev' | 'staging' | 'prod';
  /** Whether the local docker+kind build path is active. */
  localBuild: boolean;
  simulatedGcp: boolean;
  simulatedK8s: boolean;
  appsDomain: string;
}

/**
 * Fetch the server-side deploy-environment report — whether GCP and k8s are
 * real or simulated. Returns null while loading. The Hub UI uses this to
 * show honest status messages instead of claiming a simulated deploy is live.
 */
export function useDeployEnvironment(): DeployEnvironment | null {
  const { token } = useAuth();
  const [env, setEnv] = useState<DeployEnvironment | null>(null);

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const response = await fetch('/api/apps/deploy-environment', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as DeployEnvironment;
        if (!aborted) setEnv(payload);
      } catch {
        /* offline / server down — leave null so UI skips the banner */
      }
    })();
    return () => { aborted = true; };
  }, [token]);

  return env;
}

/**
 * Does the deploy pipeline actually produce a runnable app? `localBuild`
 * covers GCP's role locally, so a dev setup with simulatedGcp=true but
 * localBuild=true is NOT simulated — a real image gets built and loaded.
 * Simulation is true only when nothing is actually building the image or
 * when k8s has no real cluster to apply to.
 */
export function isSimulationMode(env: DeployEnvironment | null): boolean {
  if (env === null) return false;
  if (env.simulatedK8s) return true;
  if (env.simulatedGcp && !env.localBuild) return true;
  return false;
}
